// Stealth-Agent FE — small shared helpers (isolated; no TG imports).

export function fmtTime(d) {
  if (!d) return '—';
  try { return new Date(d).toLocaleString(); } catch { return '—'; }
}

export function fmtDuration(seconds) {
  const s = Math.round(Number(seconds) || 0);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ${s % 60}s`;
  const h = Math.floor(m / 60);
  return `${h}h ${m % 60}m`;
}

export function relativeTime(d) {
  if (!d) return 'never';
  const diff = (Date.now() - new Date(d).getTime()) / 1000;
  if (diff < 60) return `${Math.round(diff)}s ago`;
  if (diff < 3600) return `${Math.round(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.round(diff / 3600)}h ago`;
  return `${Math.round(diff / 86400)}d ago`;
}

// Liveness: the agent heartbeats every ~60s, so "online" = seen within ~2.5 min
// (tolerates one missed heartbeat without flapping). Prefer the freshest of the
// heartbeat / seen timestamps a device row carries.
const ONLINE_WINDOW_MS = 150 * 1000; // 2.5 min

export function lastSeenTs(device) {
  if (!device) return null;
  const cands = [device.last_heartbeat_at, device.last_seen_at, device.last_report_at]
    .filter(Boolean).map((d) => new Date(d).getTime());
  return cands.length ? Math.max(...cands) : null;
}

/** Version label for Devices table — avoid double "v" when DB already has fixv19 / v1.2.3. */
export function formatAgentVersion(version) {
  const s = String(version || '').trim();
  if (!s) return '';
  return /^[vV]/.test(s) || /^fix/i.test(s) ? s : `v${s}`;
}

// Accepts either a timestamp/string OR a device object (uses its freshest liveness ts).
export function isOnline(lastSeenOrDevice) {
  const ts = (lastSeenOrDevice && typeof lastSeenOrDevice === 'object' && !(lastSeenOrDevice instanceof Date))
    ? lastSeenTs(lastSeenOrDevice)
    : (lastSeenOrDevice ? new Date(lastSeenOrDevice).getTime() : null);
  if (!ts) return false;
  return (Date.now() - ts) < ONLINE_WINDOW_MS;
}

/** Primary label for a device row — name from TG login, then email (no IDs). */
export function deviceDisplayName(d) {
  if (!d) return 'Unknown';
  const name = String(d.name || d.employee_name || '').trim();
  if (name) return name;
  if (d.email) return d.email;
  return 'Team member';
}

/** Device picker label: online dot + name + optional email hint (no device id). */
export function deviceSelectLabel(d) {
  const who = deviceDisplayName(d);
  const emailHint = d.email && who !== d.email ? d.email : '';
  const parts = [who, emailHint].filter(Boolean);
  return `${isOnline(d) ? '🟢' : '⚪'} ${parts.join(' · ')}`;
}

export const PALETTE = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4', '#ec4899', '#84cc16'];

/** Ms between last heartbeat and last report (positive = heartbeat newer). */
export function heartbeatReportGapMs(device) {
  if (!device?.last_heartbeat_at || !device?.last_report_at) return null;
  return new Date(device.last_heartbeat_at).getTime() - new Date(device.last_report_at).getTime();
}

/**
 * Admin-facing activity bucket from timestamps already on the device row.
 * Distinguishes "online but idle" from "actively reporting".
 */
export function deviceActivityState(device) {
  if (!device) return { kind: 'unknown', label: '—', color: 'default', tip: '' };
  if (device.is_decommissioned) {
    return device.deletion_confirmed_at
      ? { kind: 'cleaned', label: 'Cleaned', color: 'default', tip: 'Agent removed from device' }
      : { kind: 'killed', label: 'Killed', color: 'red', tip: 'Decommission in progress or pending cleanup' };
  }
  if (!device.is_tracking_active) {
    return { kind: 'paused', label: 'Paused', color: 'orange', tip: 'Admin paused — agent will not track' };
  }
  if (!isOnline(device)) {
    return { kind: 'offline', label: 'Offline', color: 'default', tip: 'No heartbeat in the last ~2.5 min' };
  }

  const reportAt = device.last_report_at ? new Date(device.last_report_at).getTime() : 0;
  if (!reportAt) {
    return {
      kind: 'no_reports',
      label: 'No reports',
      color: 'default',
      tip: 'Agent is online but no report has been received yet',
    };
  }

  const gapSec = (Date.now() - reportAt) / 1000;
  const idleSec = Number(device.idle_threshold) || 300;
  const interval = Number(device.report_interval) || 120;
  const staleSec = idleSec + interval + 60;

  if (gapSec > staleSec) {
    const gapLabel = relativeTime(device.last_report_at);
    return {
      kind: 'idle',
      label: 'Idle · no PC use',
      color: 'gold',
      tip: `Agent online (heartbeat OK) but last report ${gapLabel}. User is likely away, locked, or not typing.`,
    };
  }

  const keys = Number(device.last_report_keys) || 0;
  const clicks = Number(device.last_report_clicks) || 0;
  const app = String(device.last_report_app || '').trim();

  if (app === 'Unknown' && keys === 0 && clicks === 0) {
    return {
      kind: 'weak',
      label: 'Weak snapshot',
      color: 'warning',
      tip: 'Short edge report before idle — not active work data',
    };
  }

  const inputHint = keys || clicks ? `${keys} keys · ${clicks} clicks` : 'no input in last report';
  return {
    kind: 'active',
    label: app || 'Active',
    color: 'green',
    tip: `Last report ${relativeTime(device.last_report_at)} · ${inputHint}`,
  };
}

/** One-line summary for the Last report column. */
export function lastReportSummary(device) {
  if (!device?.last_report_at) return { primary: 'Never', secondary: 'Click to view reports' };
  const app = String(device.last_report_app || '').trim() || '—';
  const keys = Number(device.last_report_keys) || 0;
  const clicks = Number(device.last_report_clicks) || 0;
  const ocr = Number(device.last_report_ocr_words) || 0;
  const input = keys || clicks ? `${keys}k · ${clicks}c` : '0 input';
  const ocrHint = ocr ? ` · ${ocr} OCR words` : ' · 0 OCR';
  return {
    primary: relativeTime(device.last_report_at),
    secondary: `${app} · ${input}${ocrHint}`,
  };
}
